/**
 * This component will show the booking info and calculated total price.
 * I.e. dates and other details related to payment decision in receipt format.
 */
import React, { useState } from 'react';
import { oneOf, string } from 'prop-types';
import classNames from 'classnames';

import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import {
  propTypes,
  LISTING_UNIT_TYPES,
  LINE_ITEM_CUSTOMER_COMMISSION,
  LINE_ITEM_PROVIDER_COMMISSION,
} from '../../util/types';

import LineItemBookingPeriod from './LineItemBookingPeriod';
import LineItemBasePriceMaybe from './LineItemBasePriceMaybe';
import LineItemSubTotalMaybe from './LineItemSubTotalMaybe';
import LineItemShippingFeeMaybe from './LineItemShippingFeeMaybe';
import LineItemPickupFeeMaybe from './LineItemPickupFeeMaybe';
import LineItemCustomerCommissionMaybe from './LineItemCustomerCommissionMaybe';
import LineItemCustomerCommissionRefundMaybe from './LineItemCustomerCommissionRefundMaybe';
import LineItemProviderCommissionMaybe from './LineItemProviderCommissionMaybe';
import LineItemProviderCommissionRefundMaybe from './LineItemProviderCommissionRefundMaybe';
import LineItemRefundMaybe from './LineItemRefundMaybe';
import LineItemTotalPrice from './LineItemTotalPrice';

import { convertLineItems } from '../../util/currency';
// import { staticExchangeRates } from '../../config/configStripe';
import { useDispatch, useSelector } from 'react-redux';
import LineItemAdditionalFees from './LineItemAdditionalFees';
import { manageDisableScrolling } from '../../ducks/ui.duck';
import css from './OrderBreakdown.module.css';
import IconCollection from '../IconCollection/IconCollection';
import { PrimaryButton } from '../Button/Button';
import BookingSupport from '../BookingSupport/BookingSupport';
import { H6 } from '../Heading/Heading';

const getValidCode = (code) => {
  const newCode = code.includes(':');
  const updatedCode = !newCode ? code : `line-item/${code.split('-').reverse()[0]}`
  return updatedCode;
};

const getValidLineItemCode = (lineItem) => {
  const newCode = lineItem.code.includes(':');
  const code = !newCode ? lineItem.code : `line-item/${lineItem.code.split('-').reverse()[0]}`
  return code;
}

const PickupReturnOptions = ({ transaction, code, setIsMapView }) => {
  const lineItems = transaction?.attributes?.lineItems;
  const lineItemsFromMetaData = transaction.attributes?.metadata?.lineItems;
  const updatedCode = getValidCode(code); // line items in meta data contains the default line item codes by sharetribe.
  const item = lineItemsFromMetaData?.find(item => item.code === updatedCode)
    ?? lineItems?.find(item => item.code === code); // line items in transaction contains the new line item codes

  const { pickupAtOwnerLocation, returnAtOwnerLocation } = item || {};

  return (
    <div className={css.container}>
      {pickupAtOwnerLocation && (
        <div className={css.option} onClick={() => setIsMapView(true)}>
          <span><FormattedMessage id="OrderBreakdown.pickupAtOwnerLocation" /></span>
        </div>
      )}
      {returnAtOwnerLocation && (
        <div className={css.option} onClick={() => setIsMapView(true)}>
          <span><FormattedMessage id="OrderBreakdown.returnAtOwnerLocation" /></span>
        </div>
      )}
    </div>
  );
};

export const OrderBreakdownComponent = props => {
  const {
    rootClassName,
    className,
    userRole,
    transaction,
    booking,
    intl,
    dateType,
    timeZone,
    currency,
    marketplaceName,
    checkoutButton,
    showBookingSupport,
    lineItemClass,
    lineItemTotalClass,
    breakdownTitle,
    isShowCommissionLineItem = true,
    savingPerDayMessage,
  } = props;
  
  const dispatch = useDispatch();
  const onManageDisableScrolling = (params) => dispatch(manageDisableScrolling(params));
  const [isMapView, setIsMapView] = useState(false);
  const { currentUserCurrency } = useSelector(state => state.user);
  const { exchangeRates } = useSelector(state => state.exchangeRate);

  const isCustomer = userRole === 'customer';
  const isProvider = userRole === 'provider';
  const allLineItems = transaction.attributes.lineItems || [];
  // We'll show only line-items that are specific for the current userRole (customer vs provider)
  const lineItems = allLineItems.filter(lineItem => lineItem.includeFor.includes(userRole));

  // replace static exchange rates with the real one.
  const convertedLineItems = convertLineItems(exchangeRates, lineItems, currentUserCurrency);

  const unitLineItems = convertedLineItems.filter(item => {
    // we are passing new code as 'line-item/item-1:title:unit
    // We don't want to break old code 'line-item/unit'
    const code = getValidLineItemCode(item)
    return LISTING_UNIT_TYPES.includes(code) && !item.reversal
  }
  );

  // Line-item code that matches with base unit: day, night, hour, item
  const lineItemUnitType = unitLineItems?.[0]?.code;

  const hasCommissionLineItem = convertedLineItems.find(item => {
    const hasCustomerCommission = isCustomer && item.code === LINE_ITEM_CUSTOMER_COMMISSION;
    const hasProviderCommission = isProvider && item.code === LINE_ITEM_PROVIDER_COMMISSION;
    return (hasCustomerCommission || hasProviderCommission) && !item.reversal;
  });

  const classes = classNames(rootClassName || css.root, className);

  /**
   * OrderBreakdown contains different line items:
   *
   * LineItemBookingPeriod: prints booking start and booking end types. Prop dateType
   * determines if the date and time or only the date is shown
   *
   * LineItemShippingFeeMaybe: prints the shipping fee (combining additional fee of
   * additional items into it).
   *
   * LineItemShippingFeeRefundMaybe: prints the amount of refunded shipping fee
   *
   * LineItemBasePriceMaybe: prints the base price calculation for the listing, e.g.
   * "$150.00 * 2 nights $300"
   *
   * LineItemUnknownItemsMaybe: prints the line items that are unknown. In ideal case there
   * should not be unknown line items. If you are using custom pricing, you should create
   * new custom line items if you need them.
   *
   * LineItemSubTotalMaybe: prints subtotal of line items before possible
   * commission or refunds
   *
   * LineItemRefundMaybe: prints the amount of refund
   *
   * LineItemCustomerCommissionMaybe: prints the amount of customer commission
   * The default transaction process used by this template doesn't include the customer commission.
   *
   * LineItemCustomerCommissionRefundMaybe: prints the amount of refunded customer commission
   *
   * LineItemProviderCommissionMaybe: prints the amount of provider commission
   *
   * LineItemProviderCommissionRefundMaybe: prints the amount of refunded provider commission
   *
   * LineItemTotalPrice: prints total price of the transaction
   *
   */

  return (
    <div className={classes}>
      <div className={css.lineItemsContainer}>
        {breakdownTitle && <H6 as="h3" className={css.bookingBreakdownTitle}>
          {breakdownTitle}
        </H6>}
        {savingPerDayMessage && <span className={css.savingPerDayMessage}>{savingPerDayMessage}</span>}
        {unitLineItems.map(i => {
          const lineItemUnitType = i.code;
          return (
            <div key={lineItemUnitType} >
              <LineItemBasePriceMaybe lineItems={convertedLineItems} code={lineItemUnitType} intl={intl} lineItemClass={lineItemClass} />
              <PickupReturnOptions
                transaction={transaction}
                code={lineItemUnitType}
                setIsMapView={setIsMapView}
              />
              <LineItemBookingPeriod
                booking={booking}
                code={lineItemUnitType}
                dateType={dateType}
                timeZone={timeZone}
                transaction={transaction}
                lineItemClass={lineItemClass}
              />
            </div>
          )
        })}
        <LineItemShippingFeeMaybe lineItems={convertedLineItems} intl={intl} lineItemClass={lineItemClass} />
        <LineItemPickupFeeMaybe lineItems={convertedLineItems} intl={intl} lineItemClass={lineItemClass} />
        <LineItemAdditionalFees lineItems={convertedLineItems} intl={intl} lineItemClass={lineItemClass} />
        {/* <LineItemUnknownItemsMaybe lineItems={convertedLineItems} isProvider={isProvider} intl={intl} /> */}

        <LineItemSubTotalMaybe
          lineItems={convertedLineItems}
          code={lineItemUnitType}
          userRole={userRole}
          intl={intl}
          marketplaceCurrency={currency}
          lineItemClass={lineItemClass}
        />
        <LineItemRefundMaybe lineItems={convertedLineItems} intl={intl} marketplaceCurrency={currency} lineItemClass={lineItemClass} />

        <LineItemCustomerCommissionMaybe
          lineItems={convertedLineItems}
          isCustomer={isCustomer}
          marketplaceName={marketplaceName}
          intl={intl}
          lineItemClass={lineItemClass}
        />
        <LineItemCustomerCommissionRefundMaybe
          lineItems={convertedLineItems}
          isCustomer={isCustomer}
          marketplaceName={marketplaceName}
          intl={intl}
          lineItemClass={lineItemClass}
        />

        <LineItemProviderCommissionMaybe
          lineItems={convertedLineItems}
          isProvider={isProvider}
          marketplaceName={marketplaceName}
          intl={intl}
          lineItemClass={lineItemClass}
        />
        <LineItemProviderCommissionRefundMaybe
          lineItems={convertedLineItems}
          isProvider={isProvider}
          marketplaceName={marketplaceName}
          intl={intl}
          lineItemClass={lineItemClass}
        />

        <LineItemTotalPrice transaction={transaction} isProvider={isProvider} intl={intl} lineItemClass={lineItemClass} lineItemTotalClass={lineItemTotalClass} />

        {checkoutButton && (<div className={css.checkoutButton}>
          {checkoutButton}
        </div>)
        }
      </div>
      {isShowCommissionLineItem && hasCommissionLineItem ? (
        <span className={css.feeInfo}>
          <IconCollection name="info_icon" />
          <FormattedMessage id="OrderBreakdown.commissionFeeNote" />
        </span>
      ) : null}

      {showBookingSupport && <BookingSupport />}

      {/* // : (<div>
        //   <PrimaryButton
        //     className={css.submitButton}
        //     type="submit"
        //   >
        //     <FormattedMessage
        //       id="OrderBreakdown.sendRequest"
        //     />
        //   </PrimaryButton>
        //   <div className={css.processedSecurely}><FormattedMessage id='OrderBreakdown.processedSecurely' /><IconCollection name="processedSecurely" /></div>
        // </div>) */}

    </div>
  );
};

OrderBreakdownComponent.defaultProps = {
  rootClassName: null,
  className: null,
  booking: null,
  dateType: null,
};

OrderBreakdownComponent.propTypes = {
  rootClassName: string,
  className: string,

  marketplaceName: string.isRequired,
  userRole: oneOf(['customer', 'provider']).isRequired,
  transaction: propTypes.transaction.isRequired,
  booking: propTypes.booking,
  dateType: propTypes.dateType,

  // from injectIntl
  intl: intlShape.isRequired,
};

const OrderBreakdown = injectIntl(OrderBreakdownComponent);

OrderBreakdown.displayName = 'OrderBreakdown';

export default OrderBreakdown;
